import { ArrowRight, Loader2, UserPen } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { humanizeIdentifier } from '@/features/manual-graph/entity-form'
import {
  hasSchemaRelation,
  optionKey,
  type RelationOption,
  relationOptions,
} from '@/features/manual-graph/relation-options'
import { SchemaGapNotice } from '@/features/manual-graph/SchemaGapNotice'
import { useCaseSchema } from '@/hooks/use-case-queries'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import { Label } from '../ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'

export interface ConnectionEntity {
  id: string
  name: string
  typeLabel?: string
  labels?: string[]
}

/**
 * Cap an entity name to ``maxLen`` characters with an ellipsis, so a long
 * unbreakable identifier (a raw Neo4j fallback id) does not push the grid cell
 * wider than the dialog. The full name stays in ``title``.
 */
const NAME_MAX_LEN = 32

function truncateName(name: string, maxLen: number = NAME_MAX_LEN): string {
  if (!name) return name
  return name.length > maxLen ? `${name.slice(0, maxLen - 1)}…` : name
}

export interface CreateRelationshipDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Case the source/target nodes belong to — used to resolve the schema. */
  caseId: string
  sourceEntity: ConnectionEntity | null
  targetEntity: ConnectionEntity | null
  /** A `reverse` option runs the edge from the target to the source, as the schema declares it. */
  onCreateRelationship: (option: RelationOption) => Promise<void>
}

/**
 * Pick how two entities are related. The options are the case schema's
 * patterns between the two types (either way round) plus POSSIBLE_MATCH; a
 * relationship the schema lacks is added in the Schema Builder first.
 */
export function CreateRelationshipDialog({
  open,
  onOpenChange,
  caseId,
  sourceEntity,
  targetEntity,
  onCreateRelationship,
}: CreateRelationshipDialogProps) {
  const { t } = useTranslation()
  const [selectedKey, setSelectedKey] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)
  const schemaQ = useCaseSchema(caseId)
  const sourceType = sourceEntity?.typeLabel
  const targetType = targetEntity?.typeLabel
  const options = useMemo(
    () => relationOptions(schemaQ.data, sourceType, targetType),
    [schemaQ.data, sourceType, targetType]
  )

  // Keep a still-valid choice; otherwise preselect the first schema relation.
  useEffect(() => {
    if (options.some(o => optionKey(o) === selectedKey)) return
    const preferred = options.find(o => !o.system) ?? options[0]
    setSelectedKey(preferred ? optionKey(preferred) : '')
  }, [options, selectedKey])

  if (!sourceEntity || !targetEntity) return null
  const selected = options.find(o => optionKey(o) === selectedKey)

  const handleCreate = async () => {
    if (!selected) return
    setIsProcessing(true)
    try {
      await onCreateRelationship(selected)
      onOpenChange(false)
    } finally {
      setIsProcessing(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* `sm:max-w-2xl`, not `max-w-2xl`: the base classes set `sm:max-w-sm`,
          which an unprefixed utility cannot outrank at the `sm` screen. */}
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('manualGraph.relationship.title')}</DialogTitle>
          <DialogDescription>{t('manualGraph.relationship.description')}</DialogDescription>
        </DialogHeader>

        {/* `min-w-0`: DialogContent is a grid, whose items default to
            `min-width: auto`; a long description would otherwise widen the
            wrapper past the dialog and get clipped. */}
        <div className="w-full min-w-0 space-y-4 py-2 md:space-y-5">
          <div className="grid grid-cols-2 gap-2 md:gap-4">
            <EndpointCard entity={sourceEntity} role={t('manualGraph.relationship.source')} />
            <EndpointCard entity={targetEntity} role={t('manualGraph.relationship.target')} />
          </div>

          <div className="space-y-3">
            <Label htmlFor="relationship-type">{t('manualGraph.relationship.type')}</Label>
            <Select value={selectedKey} onValueChange={setSelectedKey} disabled={schemaQ.isLoading}>
              <SelectTrigger id="relationship-type" className="w-full">
                <SelectValue
                  placeholder={
                    schemaQ.isLoading
                      ? t('manualGraph.relationship.loading')
                      : t('manualGraph.relationship.placeholder')
                  }
                />
              </SelectTrigger>
              <SelectContent className="max-h-[400px] z-[250]">
                {options.map(option => (
                  <SelectItem key={optionKey(option)} value={optionKey(option)}>
                    <RelationOptionLabel option={option} />
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {selected && (
              <RelationPreview option={selected} source={sourceEntity} target={targetEntity} />
            )}
          </div>

          <SchemaGapNotice
            caseId={caseId}
            gap="missingRelation"
            sourceType={sourceType ? humanizeIdentifier(sourceType) : sourceEntity.name}
            targetType={targetType ? humanizeIdentifier(targetType) : targetEntity.name}
            onlySystemRelation={!schemaQ.isLoading && !hasSchemaRelation(options)}
          />
        </div>

        <DialogFooter className="items-center gap-3 sm:justify-between">
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <UserPen className="size-3.5 shrink-0" aria-hidden />
            {t('manualGraph.relationship.provenance')}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isProcessing}>
              {t('common.cancel')}
            </Button>
            <Button variant="solid" onClick={handleCreate} disabled={isProcessing || !selected}>
              {isProcessing ? (
                <>
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                  {t('manualGraph.relationship.creating')}
                </>
              ) : (
                t('manualGraph.relationship.create')
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function EndpointCard({ entity, role }: { entity: ConnectionEntity; role: string }) {
  const { t } = useTranslation()
  return (
    <div className="min-w-0 rounded-lg border border-border bg-muted/50 p-3 md:p-4">
      <div className="mb-2 flex items-center gap-2">
        <Badge variant="outline">
          {entity.typeLabel ? humanizeIdentifier(entity.typeLabel) : t('manualGraph.unknownType')}
        </Badge>
      </div>
      <h4 className="break-words text-base font-semibold md:text-lg" title={entity.name}>
        {truncateName(entity.name)}
      </h4>
      <p className="mt-1 text-sm text-muted-foreground">{role}</p>
    </div>
  )
}

function RelationOptionLabel({ option }: { option: RelationOption }) {
  const { t } = useTranslation()
  const detail = option.system
    ? t('manualGraph.relationship.systemHint')
    : option.direction === 'reverse'
      ? t('manualGraph.relationship.reversedShort')
      : option.description
  return (
    // `min-w-0` + `truncate` keep a long schema description inside the trigger.
    <div className="flex min-w-0 items-center gap-2">
      <span className="shrink-0 font-medium">{humanizeIdentifier(option.type)}</span>
      {detail && <span className="min-w-0 truncate text-xs text-muted-foreground">{detail}</span>}
    </div>
  )
}

/** The edge as it will be written — reversed options run target → source. */
function RelationPreview({
  option,
  source,
  target,
}: {
  option: RelationOption
  source: ConnectionEntity
  target: ConnectionEntity
}) {
  const { t } = useTranslation()
  const [from, to] = option.direction === 'reverse' ? [target, source] : [source, target]
  return (
    <div className="min-w-0 space-y-1 rounded-lg border border-border bg-muted/50 p-3">
      {option.description && (
        <p className="break-words text-sm text-foreground">{option.description}</p>
      )}
      <p className="flex min-w-0 flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
        <span>{t('manualGraph.relationship.preview')}</span>
        <span className="font-medium text-foreground" title={from.name}>
          {truncateName(from.name)}
        </span>
        <ArrowRight className="size-3 shrink-0" aria-hidden />
        <span className="font-mono text-primary">{option.type}</span>
        <ArrowRight className="size-3 shrink-0" aria-hidden />
        <span className="font-medium text-foreground" title={to.name}>
          {truncateName(to.name)}
        </span>
      </p>
      {option.direction === 'reverse' && (
        <p className="text-xs text-muted-foreground">
          {t('manualGraph.relationship.reversedHint', { source: from.name, target: to.name })}
        </p>
      )}
    </div>
  )
}
